import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Tag, Typography, Space, Button, Modal, Form, Input, Select, message, Popconfirm, Badge } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, TagsOutlined } from '@ant-design/icons';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../api';

const { Title, Text } = Typography;
const { Option } = Select;

const PRESET_COLORS = ['#f5222d','#fa541c','#fa8c16','#faad14','#52c41a','#13c2c2','#1890ff','#722ed1','#eb2f96','#0F1D6B'];

export default function CategoryMaster() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [modalOpen, setModalOpen]   = useState(false);
  const [editing, setEditing]       = useState(null);
  const [selectedColor, setSelectedColor] = useState('#1890ff');
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    getCategories().then(r => setCategories(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setSelectedColor('#1890ff'); form.resetFields(); form.setFieldValue('color', '#1890ff'); setModalOpen(true); };
  const openEdit   = (rec) => { setEditing(rec); setSelectedColor(rec.color || '#1890ff'); form.setFieldsValue(rec); setModalOpen(true); };

  const handleSave = async () => {
    try {
      const vals = await form.validateFields();
      if (editing) { await updateCategory(editing.id, vals); message.success('Category updated'); }
      else         { await createCategory(vals);             message.success('Category created'); }
      setModalOpen(false); load();
    } catch (e) { if (!e.errorFields) message.error(e.response?.data?.message || 'Save failed'); }
  };

  const columns = [
    { title: '#', width: 50, render: (_, __, i) => i + 1 },
    { title: 'Color', dataIndex: 'color', width: 70, render: v => <div style={{ width: 32, height: 32, borderRadius: 6, background: v, margin: '0 auto' }} /> },
    { title: 'Category Name', dataIndex: 'name', render: (v, r) => <Tag color={r.color} style={{ fontSize: 14, padding: '4px 12px' }}>{v}</Tag>, sorter: (a, b) => a.name.localeCompare(b.name) },
    { title: 'Code', dataIndex: 'code', render: v => <Tag>{v}</Tag>, width: 100 },
    { title: 'Description', dataIndex: 'description', render: v => v || '-', ellipsis: true },
    { title: 'Delegates', dataIndex: 'delegate_count', render: v => <Badge count={v || 0} style={{ background: '#0F1D6B' }} showZero />, width: 100, align: 'center' },
    { title: 'Status', dataIndex: 'status', render: v => <Tag color={v === 'active' ? 'green' : 'default'}>{v}</Tag>, width: 90 },
    {
      title: 'Actions', width: 100, render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Delete category?" onConfirm={() => deleteCategory(r.id).then(() => { message.success('Deleted'); load(); })}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: '#0F1D6B' }}>
            <TagsOutlined style={{ marginRight: 8 }} />Category Master
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Manage delegate categories (VIP, Speaker, Delegate, etc.)</Text>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ background: '#0F1D6B' }}>
            Add Category
          </Button>
        </Col>
      </Row>

      {/* Color preview cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {categories.map(c => (
          <Col key={c.id}>
            <Card size="small" style={{ borderRadius: 10, borderTop: `4px solid ${c.color}`, minWidth: 100, textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>{c.delegate_count || 0}</div>
              <Tag color={c.color} style={{ fontSize: 11 }}>{c.name}</Tag>
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ borderRadius: 10 }}>
        <Table columns={columns} dataSource={categories} rowKey="id" loading={loading}
          size="small" pagination={{ pageSize: 15 }} />
      </Card>

      <Modal title={editing ? 'Edit Category' : 'Add Category'} open={modalOpen}
        onOk={handleSave} onCancel={() => setModalOpen(false)}
        okButtonProps={{ style: { background: '#0F1D6B' } }}>
        <Form form={form} layout="vertical" size="middle">
          <Row gutter={16}>
            <Col span={14}>
              <Form.Item name="name" label="Category Name" rules={[{ required: true }]}>
                <Input placeholder="e.g. VIP, Speaker, Delegate" />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="code" label="Short Code" rules={[{ required: true }]}>
                <Input placeholder="VIP" maxLength={10} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="color" label="Badge Color">
            <Row gutter={8} align="middle">
              {PRESET_COLORS.map(c => (
                <Col key={c}>
                  <div onClick={() => { setSelectedColor(c); form.setFieldValue('color', c); }}
                    style={{ width: 28, height: 28, borderRadius: 6, background: c, cursor: 'pointer', border: selectedColor === c ? '3px solid #000' : '2px solid transparent', transition: 'border 0.2s' }} />
                </Col>
              ))}
            </Row>
            <Input prefix={<div style={{ width: 16, height: 16, borderRadius: 3, background: selectedColor, marginRight: 4 }} />}
              value={selectedColor} onChange={e => { setSelectedColor(e.target.value); form.setFieldValue('color', e.target.value); }}
              placeholder="#1890ff" style={{ marginTop: 8, width: 160 }} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="active">
            <Select>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
